import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from '@/auth/AuthProvider';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { AppShell } from '@/components/layout/AppShell';
import { ThemeProvider } from '@/theme/ThemeProviderRoot';
import { TooltipProvider } from '@/components/ui';
import { Home } from '@/pages/Home';
import { Marketplace } from '@/pages/Marketplace';
import { Portfolio } from '@/pages/Portfolio';
import { Settings, SettingsProfile } from '@/pages/Settings';
import { Login } from '@/pages/Login';
import { NotFound } from '@/pages/NotFound';
import { DesignGallery } from '@/pages/design/DesignGallery';
import { Showcase } from '@/pages/design/showcase/Showcase';
import { Character } from '@/pages/design/character/Character';
import { Foundation } from '@/pages/design/foundation/Foundation';
import { WorkspacePatterns } from '@/pages/design/workspace/WorkspacePatterns';
import { ConversationsPatterns } from '@/pages/design/conversations/ConversationsPatterns';
import { OperationsPatterns } from '@/pages/design/operations/OperationsPatterns';
import { ProgressPatterns } from '@/pages/design/progress/ProgressPatterns';
import { DutyPatterns } from '@/pages/design/duty/DutyPatterns';
import { LoginLab } from '@/pages/design/login/LoginLab';
import { ObserverPlayground } from '@/pages/design/observer/ObserverPlayground';
import { CompanionLab } from '@/pages/design/observer/CompanionLab';
import { LiquidLab } from '@/pages/design/observer/LiquidLab';

const CommentsPatterns = lazy(() =>
  import('@/pages/design/comments/CommentsPatterns').then((module) => ({ default: module.CommentsPatterns })),
);

const FieldsPatterns = lazy(() =>
  import('@/pages/design/fields/FieldsPatterns').then((m) => ({ default: m.FieldsPatterns })),
);

const ChartsPatterns = lazy(() =>
  import('@/pages/design/charts/ChartsPatterns').then((m) => ({ default: m.ChartsPatterns })),
);

const ObjectsPatterns = lazy(() =>
  import('@/pages/design/objects/ObjectsPatterns').then((m) => ({ default: m.ObjectsPatterns })),
);

const ObjectFormsPatterns = lazy(() =>
  import('@/pages/design/object-forms/ObjectFormsPatterns').then((m) => ({ default: m.ObjectFormsPatterns })),
);

const ObjectRelationsPatterns = lazy(() =>
  import('@/pages/design/object-relations/ObjectRelationsPatterns').then((m) => ({
    default: m.ObjectRelationsPatterns,
  })),
);

const ObjectViewsPatterns = lazy(() =>
  import('@/pages/design/object-views/ObjectViewsPatterns').then((m) => ({ default: m.ObjectViewsPatterns })),
);

const HomePatterns = lazy(() =>
  import('@/pages/design/home/HomePatterns').then((m) => ({ default: m.HomePatterns })),
);

export function App() {
  return (
    <ThemeProvider>
      <TooltipProvider delayDuration={300} skipDelayDuration={500}>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              {/* Публичный — без AppShell */}
              <Route path="/login" element={<Login />} />
              {/* Галерея дизайн-системы — без данных, поэтому без auth (DESIGN.md, R16). */}
              <Route path="/design" element={<DesignGallery />} />
              <Route path="/design/workspace/*" element={<WorkspacePatterns />} />
              <Route path="/design/operations/*" element={<OperationsPatterns />} />
              <Route path="/design/conversations/*" element={<ConversationsPatterns />} />
              <Route path="/design/progress/*" element={<ProgressPatterns />} />
              <Route path="/design/duty/*" element={<DutyPatterns />} />
              <Route
                path="/design/comments/*"
                element={
                  <Suspense
                    fallback={
                      <div role="status" className="min-h-screen bg-frame p-6 text-sm text-fg-muted">
                        Открываем обсуждение…
                      </div>
                    }
                  >
                    <CommentsPatterns />
                  </Suspense>
                }
              />
              <Route
                path="/design/fields"
                element={
                  <Suspense
                    fallback={
                      <div role="status" className="min-h-screen bg-frame p-6 text-sm text-fg-muted">
                        Открываем поля…
                      </div>
                    }
                  >
                    <FieldsPatterns />
                  </Suspense>
                }
              />
              <Route
                path="/design/charts"
                element={
                  <Suspense
                    fallback={
                      <div role="status" className="min-h-screen bg-frame p-6 text-sm text-fg-muted">
                        Открываем графики…
                      </div>
                    }
                  >
                    <ChartsPatterns />
                  </Suspense>
                }
              />
              <Route
                path="/design/objects/*"
                element={
                  <Suspense
                    fallback={
                      <div role="status" className="min-h-screen bg-frame p-6 text-sm text-fg-muted">
                        Открываем объекты…
                      </div>
                    }
                  >
                    <ObjectsPatterns />
                  </Suspense>
                }
              />
              <Route
                path="/design/object-forms"
                element={
                  <Suspense
                    fallback={
                      <div role="status" className="min-h-screen bg-frame p-6 text-sm text-fg-muted">
                        Открываем образец…
                      </div>
                    }
                  >
                    <ObjectFormsPatterns />
                  </Suspense>
                }
              />
              <Route
                path="/design/object-relations"
                element={
                  <Suspense
                    fallback={
                      <div role="status" className="min-h-screen bg-frame p-6 text-sm text-fg-muted">
                        Открываем образец…
                      </div>
                    }
                  >
                    <ObjectRelationsPatterns />
                  </Suspense>
                }
              />
              <Route
                path="/design/object-views"
                element={
                  <Suspense
                    fallback={
                      <div role="status" className="min-h-screen bg-frame p-6 text-sm text-fg-muted">
                        Открываем образец…
                      </div>
                    }
                  >
                    <ObjectViewsPatterns />
                  </Suspense>
                }
              />
              <Route
                path="/design/home"
                element={
                  <Suspense
                    fallback={
                      <div role="status" className="min-h-screen bg-frame p-6 text-sm text-fg-muted">
                        Открываем образец…
                      </div>
                    }
                  >
                    <HomePatterns />
                  </Suspense>
                }
              />
              <Route path="/design/showcase" element={<Showcase />} />
              {/* «Основа»: сайт целиком на примерных данных — без входа (docs/foundation.md). */}
              <Route path="/design/foundation/*" element={<Foundation />} />
              {/* Вход по концепции Kaname, вход имитирован — переход в «Основу» (docs/brand-concept.md). */}
              <Route path="/design/login" element={<LoginLab />} />
              <Route path="/design/character" element={<Character />} />
              <Route path="/design/observer" element={<ObserverPlayground />} />
              <Route path="/design/observer/lab" element={<CompanionLab />} />
              <Route path="/design/observer/liquid" element={<LiquidLab />} />

              {/* Защищённые — в AppShell. NotFound тоже в shell'е чтобы юзер
                  всегда имел навигацию обратно. */}
              <Route
                element={
                  <ProtectedRoute>
                    <AppShell />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Home />} />
                <Route path="marketplace" element={<Marketplace />} />
                <Route path="portfolio/me" element={<Portfolio />} />
                <Route path="settings" element={<Settings />} />
                <Route path="settings/profile" element={<SettingsProfile />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  );
}
